import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Printer, RefreshCw, Search, RotateCcw, Download, Copy, FileSpreadsheet } from "lucide-react";
import { api, formatApiError, downloadFile } from "@/lib/api";
import { LabelsTable } from "@/components/LabelsTable";
import { Pager } from "@/components/Pager";
import { DateBar, todayIST } from "@/components/DateBar";
import { PrintDialog } from "@/components/PrintDialog";
import { AssignSkuDialog } from "@/components/AssignSkuDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function LabelsPage() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "pending";
  const warehouse = params.get("warehouse") || "all";
  const marketplace = params.get("marketplace") || "all";
  const batch = params.get("batch") || "";
  const duplicates = params.get("duplicates") === "1";
  const date = params.get("date") || todayIST();
  const page = Math.max(1, Number(params.get("page") || 1));
  const pageSize = Number(params.get("size") || 100);
  const [search, setSearch] = useState(params.get("q") || "");
  const [labels, setLabels] = useState([]);
  const [meta, setMeta] = useState({ total: 0, pages: 1, pending_total: 0 });
  const [warehouses, setWarehouses] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [printIds, setPrintIds] = useState(null);
  const [assign, setAssign] = useState(null);
  const [loading, setLoading] = useState(false);

  const setParam = (k, v, keepAll = false) => { const p = new URLSearchParams(params); if (v && (keepAll || v !== "all")) p.set(k, v); else p.delete(k); if (k !== "page") p.delete("page"); setParams(p); };

  useEffect(() => {
    const t = setTimeout(() => { if ((params.get("q") || "") !== search) setParam("q", search); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const filterParams = useMemo(() => ({ status, warehouse_id: warehouse, marketplace, batch_id: batch || undefined, search: params.get("q") || undefined, duplicates: duplicates || undefined, date: batch ? "all" : date }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [status, warehouse, marketplace, batch, duplicates, params.get("q"), date]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([api.get("/labels/page", { params: { ...filterParams, page, page_size: pageSize } }), api.get("/stats")]);
      setLabels(l.data.items);
      setMeta({ total: l.data.total, pages: l.data.pages, pending_total: l.data.pending_total });
      setWarehouses(s.data.warehouses);
      setSelected((prev) => new Set([...prev].filter((id) => l.data.items.some((x) => x.id === id))));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filterParams, page, pageSize]);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(labels.every((l) => selected.has(l.id)) ? new Set() : new Set(labels.map((l) => l.id)));

  const printable = useMemo(() => [...selected].filter((id) => labels.find((l) => l.id === id)), [selected, labels]);
  const pendingCount = meta.pending_total;

  const printAllPending = async () => {
    try {
      const { data } = await api.get("/labels/ids", { params: { ...filterParams, status: "pending" } });
      if (!data.ids.length) return toast.info("No pending labels in this view");
      setPrintIds(data.ids);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resetToPending = async () => {
    if (!printable.length) return;
    await Promise.all(printable.map((id) => api.patch(`/labels/${id}`, { status: "pending" })));
    toast.success(`${printable.length} label(s) reset to pending`);
    load();
  };

  const markNotDuplicate = async (id) => {
    try { await api.patch(`/labels/${id}`, { duplicate: false }); toast.success("Marked as not a duplicate"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="labels-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-widest text-emerald-400">Sorted: Warehouse ↑ then SKU ↑ · {batch ? "batch view" : date === "all" ? "all dates" : date === todayIST() ? "today" : date}</div>
          <h1 className="font-heading text-3xl font-extrabold uppercase tracking-tight sm:text-4xl lg:text-5xl">Labels</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="print-all-pending-button" variant="outline" className="border-slate-700 bg-transparent hover:bg-slate-800" disabled={!pendingCount} onClick={printAllPending}>
            <Printer className="mr-2 h-4 w-4" /> Print all pending in view ({pendingCount})
          </Button>
          <Button data-testid="bulk-print-button" className="bg-blue-600 hover:bg-blue-700" disabled={!printable.length} onClick={() => setPrintIds(printable)}>
            <Printer className="mr-2 h-4 w-4" /> Print selected ({printable.length})
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-md border border-slate-800 bg-[#111827] p-3">
        {!batch && <DateBar value={date} onChange={(d) => setParam("date", d, true)} testId="labels-date-bar" />}
        <Select value={status} onValueChange={(v) => setParam("status", v)}>
          <SelectTrigger data-testid="filter-status-select" className="w-36 border-slate-700 bg-slate-950 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent className="border-slate-700 bg-slate-900">
            <SelectItem value="pending">Pending</SelectItem><SelectItem value="printed">Printed</SelectItem><SelectItem value="all">All statuses</SelectItem>
          </SelectContent>
        </Select>
        <Select value={warehouse} onValueChange={(v) => setParam("warehouse", v)}>
          <SelectTrigger data-testid="filter-warehouse-select" className="w-44 border-slate-700 bg-slate-950 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent className="border-slate-700 bg-slate-900">
            <SelectItem value="all">All warehouses</SelectItem>
            <SelectItem value="unmatched">Unmatched only</SelectItem>
            {warehouses.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={marketplace} onValueChange={(v) => setParam("marketplace", v)}>
          <SelectTrigger data-testid="filter-marketplace-select" className="w-40 border-slate-700 bg-slate-950 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent className="border-slate-700 bg-slate-900">
            <SelectItem value="all">All marketplaces</SelectItem><SelectItem value="amazon">Amazon</SelectItem><SelectItem value="flipkart">Flipkart</SelectItem><SelectItem value="firstcry">Firstcry</SelectItem><SelectItem value="myntra">Myntra</SelectItem><SelectItem value="unknown">Unknown</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-500" />
          <Input data-testid="labels-search-input" placeholder="Search SKU, order, tracking…" value={search} onChange={(e) => setSearch(e.target.value)} className="border-slate-700 bg-slate-950 pl-8 text-xs" />
        </div>
        {batch && <Button size="sm" variant="ghost" onClick={() => setParam("batch", "")} data-testid="clear-batch-filter">Clear batch filter</Button>}
        <Button data-testid="filter-duplicates-toggle" size="sm" variant={duplicates ? "default" : "ghost"} className={duplicates ? "bg-orange-700 hover:bg-orange-800" : "text-orange-300"} onClick={() => setParam("duplicates", duplicates ? "" : "1")}>
          <Copy className="mr-1 h-4 w-4" /> Duplicates only
        </Button>
        {warehouse === "unmatched" && (
          <Button data-testid="export-unmatched-button" size="sm" variant="ghost" className="text-red-300" onClick={() => downloadFile("/labels/unmatched-export", "unmatched-skus.csv").catch((e) => toast.error(formatApiError(e)))}>
            <Download className="mr-1 h-4 w-4" /> Export unmatched CSV
          </Button>
        )}
        <Button data-testid="labels-export-button" variant="outline" className="border-slate-700 bg-transparent hover:bg-slate-800" title="Download Excel of all labels in this view" disabled={!meta.total}
          onClick={() => { const qs = new URLSearchParams(Object.fromEntries(Object.entries(filterParams).filter(([, v]) => v !== undefined))).toString(); downloadFile(`/labels/export?${qs}`, "labels.xlsx").catch((e) => toast.error(formatApiError(e))); }}>
          <FileSpreadsheet className="mr-1 h-4 w-4" /> Excel
        </Button>
        <Button data-testid="labels-refresh-button" size="icon" variant="ghost" onClick={load} title="Refresh"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></Button>
        {status === "printed" && printable.length > 0 && (
          <Button data-testid="reset-pending-button" size="sm" variant="ghost" className="text-amber-300" onClick={resetToPending}><RotateCcw className="mr-1 h-4 w-4" /> Reset to pending</Button>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-mono text-xs text-slate-400" data-testid="labels-count">
          {meta.total ? `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, meta.total)} of ${meta.total}` : "0 labels"} · {selected.size} selected on this page
        </div>
        <Pager page={page} pages={meta.pages} pageSize={pageSize} onPage={(p) => setParam("page", String(p))} onSize={(s) => setParam("size", String(s))} />
      </div>
      <LabelsTable labels={labels} selected={selected} onToggle={toggle} onToggleAll={toggleAll} onPrintOne={(id) => setPrintIds([id])} onAssign={setAssign} onNotDuplicate={markNotDuplicate} offset={(page - 1) * pageSize} />
      <div className="flex justify-end"><Pager page={page} pages={meta.pages} pageSize={pageSize} onPage={(p) => setParam("page", String(p))} onSize={(s) => setParam("size", String(s))} bottom /></div>

      <PrintDialog labelIds={printIds} open={!!printIds} onOpenChange={(o) => !o && setPrintIds(null)} onCompleted={() => { setSelected(new Set()); load(); }} onReplaceIds={setPrintIds} />
      <AssignSkuDialog label={assign} open={!!assign} onOpenChange={(o) => !o && setAssign(null)} onSaved={load} />
    </div>
  );
}
